<template>
  <transition name="tp-fade">
    <div v-if="open" class="tp-overlay" role="dialog" aria-modal="true" aria-labelledby="tp-title" @click.self="close">
      <div class="tp-dialog">
        <header class="tp-header">
          <div>
            <div id="tp-title" class="tp-title">Buy IA Explanation tokens</div>
            <div class="tp-subtitle">
              Each detailed AI explanation costs <b>1 token</b>. Choose a pack below — tokens never expire.
            </div>
          </div>
          <button class="tp-close" title="Close" @click="close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </header>

        <div class="tp-balance">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="color:var(--warning)"><circle cx="12" cy="12" r="10"/></svg>
          <span>Current balance:</span>
          <b>{{ tokens.left }}</b>
          <span class="tp-balance-sep">/</span>
          <span>{{ tokens.total }} total</span>
        </div>

        <div class="tp-packs">
          <div v-for="pack in packs" :key="pack.id"
               class="tp-pack"
               :class="{ popular: pack.popular, processing: processingId === pack.id }"
               @click="buy(pack)">
            <div v-if="pack.popular" class="tp-pack-badge">Most popular</div>
            <div class="tp-pack-amount">
              <span class="tp-pack-num">{{ pack.tokens }}</span>
              <span class="tp-pack-unit">tokens</span>
            </div>
            <div class="tp-pack-price">
              <span class="tp-pack-currency">$</span><span>{{ pack.price.toFixed(2) }}</span>
            </div>
            <div class="tp-pack-rate">${{ (pack.price / pack.tokens).toFixed(2) }} / token</div>
            <div v-if="pack.bonus" class="tp-pack-bonus">+{{ pack.bonus }}% bonus</div>
            <button class="tp-pack-btn" :disabled="processingId !== null">
              <template v-if="processingId === pack.id">
                <svg class="tp-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 12a9 9 0 1 1-6.36-8.59"/></svg>
                Processing…
              </template>
              <template v-else>Buy</template>
            </button>
          </div>
        </div>

        <footer class="tp-footer">
          <span class="tp-footer-note">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Secure checkout — billing handled by Skillforge S.L.
          </span>
          <button class="btn-ghost" @click="close">Maybe later</button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'TokenPurchaseModal',
  data() {
    return {
      processingId: null,
      packs: [
        { id: 'starter', tokens: 10,  price:  4.99, popular: false, bonus: 0  },
        { id: 'plus',    tokens: 50,  price: 19.99, popular: true,  bonus: 0  },
        { id: 'pro',     tokens: 200, price: 59.99, popular: false, bonus: 25 },
      ],
    };
  },
  computed: {
    open() { return this.$store.state.ui.tokenPurchaseOpen; },
    tokens() { return this.$store.state.tokens; },
  },
  watch: {
    open(val) {
      if (val) {
        this.processingId = null;
        document.addEventListener('keydown', this.onKey);
      } else {
        document.removeEventListener('keydown', this.onKey);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey);
  },
  methods: {
    close() { this.$store.commit('CLOSE_TOKEN_PURCHASE'); },
    onKey(e) { if (e.key === 'Escape') this.close(); },
    buy(pack) {
      if (this.processingId) return;
      this.processingId = pack.id;
      // A11 (§5.9 #21): el checkout de compra REAL queda FUERA DE ALCANCE. No hay
      // pasarela de pago aquí. Para mantener el flujo demostrable, acreditamos los
      // tokens vía el endpoint de ajuste server-side (`grantTokens`), que es la
      // ruta de seed/admin — no cobra. Sustituir por el checkout real (Magento/
      // Stripe) cuando exista.
      const credited = pack.tokens + Math.floor(pack.tokens * (pack.bonus || 0) / 100);
      this.$store.dispatch('grantTokens', credited)
        .then(() => {
          this.$store.dispatch('toast', `Purchase successful — ${credited} tokens added to your balance`);
        })
        .catch(() => {
          this.$store.dispatch('toast', 'Could not update your token balance');
        })
        .finally(() => {
          this.processingId = null;
          this.close();
        });
    },
  },
};
</script>

<style scoped>
.tp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 31, 60, 0.55);
  backdrop-filter: blur(2px);
  display: grid;
  place-items: center;
  z-index: 600;
  padding: 24px;
}
.tp-dialog {
  background: var(--card);
  border-radius: 14px;
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  overflow: auto;
  box-shadow: 0 24px 60px rgba(0,0,0,0.30);
  border: 1px solid var(--border);
}
.tp-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 26px 14px;
  border-bottom: 1px solid var(--border-soft);
}
.tp-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
  margin-bottom: 4px;
}
.tp-subtitle {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.5;
}
.tp-close {
  margin-left: auto;
  background: none;
  border: none;
  color: var(--muted);
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
}
.tp-close:hover { background: var(--bg); color: var(--ink); }

.tp-balance {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 26px;
  font-size: 12.5px;
  color: var(--ink-3);
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border-soft);
}
.tp-balance b { color: var(--ink); font-weight: 700; }
.tp-balance-sep { color: var(--muted-3); }

.tp-packs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  padding: 22px 26px;
}
@media (max-width: 700px) {
  .tp-packs { grid-template-columns: 1fr; }
}
.tp-pack {
  position: relative;
  background: var(--card);
  border: 2px solid var(--border);
  border-radius: 12px;
  padding: 20px 18px 18px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.tp-pack:hover {
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,128,201,0.15);
}
.tp-pack.popular {
  border-color: var(--accent);
  background: linear-gradient(180deg, var(--accent-soft) 0%, var(--card) 70%);
}
.tp-pack.processing { opacity: 0.7; pointer-events: none; }
.tp-pack-badge {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--accent);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 12px;
  white-space: nowrap;
}
.tp-pack-amount {
  display: flex;
  align-items: baseline;
  gap: 5px;
  justify-content: center;
  margin-top: 6px;
}
.tp-pack-num {
  font-size: 30px;
  font-weight: 800;
  color: var(--ink);
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.tp-pack-unit { font-size: 12px; color: var(--muted); font-weight: 500; }
.tp-pack-price {
  font-size: 22px;
  font-weight: 700;
  color: var(--accent-dark);
  display: inline-flex;
  align-items: baseline;
  gap: 1px;
  margin-top: 4px;
}
.tp-pack-currency { font-size: 14px; }
.tp-pack-rate {
  font-size: 11.5px;
  color: var(--muted);
  margin-bottom: 4px;
}
.tp-pack-bonus {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  color: #047857;
  background: #d1fae5;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: 0.04em;
}
.tp-pack-btn {
  margin-top: 10px;
  width: 100%;
  padding: 8px 14px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.13s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}
.tp-pack-btn:hover:not(:disabled) { background: var(--accent-dark); }
.tp-pack-btn:disabled { cursor: default; opacity: 0.85; }
.tp-spinner { animation: tp-spin 0.9s linear infinite; }
@keyframes tp-spin { to { transform: rotate(360deg); } }

.tp-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  padding: 14px 26px 22px;
  border-top: 1px solid var(--border-soft);
}
.tp-footer-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: var(--muted);
}

.tp-fade-enter-active, .tp-fade-leave-active {
  transition: opacity 0.18s ease;
}
.tp-fade-enter-active .tp-dialog, .tp-fade-leave-active .tp-dialog {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.tp-fade-enter, .tp-fade-leave-to { opacity: 0; }
.tp-fade-enter .tp-dialog, .tp-fade-leave-to .tp-dialog {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}
</style>
